import React, { useEffect } from "react";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
} from "react-router-dom";
import { AdminLayout } from "../layouts";
import {
  Auth,
  UsersV2,
  Assistance,
  VirtualScenes,
  ClientDBcreator,
  ClientDB,
  Page_Partner_Profile,
  Page_Partner_Institution,
  MozillaHubRooms,
  Page_PlanesEduca,
  Page_Billings,
  Simulators,
  Servers,
  Page_Dashboard,
  Page_DashboardPartners,
  Page_DashboardInstitutions,
  Page_DashboardTeachers,
  Page_DashboardStudents,
  Page_InstitutionDetail,
  Page_Educajob,
  Page_Video360,
  Page_EducaFiles,
  Page_REVI,
  Page_Formacion,
  Page_Leads,
  Page_LeadDetail,
  Page_LiveGames,
} from "../pages/admin";
import { TiptapTestPage } from "../pages/admin/Tiptap/TiptapTestPage";
import { useAuth } from "../hooks";

function RedirectInstitutionDetail() {
  const { id } = useParams();
  return <Navigate to={`/admin/dashboard/institutions/${id}`} replace />;
}

// M08 - Redirects de las 5 URLs legacy del front Educajob hacia la pagina
// agrupada `/admin/educajob?tab=X`. Mantiene compatibilidad con bookmarks.
function RedirectEducajobTab({ tab }) {
  return <Navigate to={`/admin/educajob?tab=${tab}`} replace />;
}

// M11 - Redirect de la URL legacy /admin/reviscene hacia la pagina
// agrupada /admin/revi?tab=scenes. Mantiene compatibilidad con bookmarks.
function RedirectReviSceneToTab() {
  return <Navigate to="/admin/revi?tab=scenes" replace />;
}

export function AdminRouter() {
  const { user, login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromUnity = params.get("fromUnity");
    const tokenFromUrl = params.get("token");
    const userFromUrl = params.get("user");

    //console.log("fromUnity:", fromUnity);
    //console.log("Token desde la URL:", tokenFromUrl);
    //console.log("Datos del usuario desde la URL:", userFromUrl);

    if (fromUnity === "true") {
      if (tokenFromUrl && userFromUrl) {
        try {
          // Guardar los datos en localStorage para futuras sesiones
          localStorage.setItem("accessToken", tokenFromUrl);
          localStorage.setItem("user", userFromUrl);

          // Parsear los datos del usuario
          const parsedUser = JSON.parse(decodeURIComponent(userFromUrl));
          console.log("Usuario parseado desde la URL:", parsedUser);

          // Llamar al método `login` con los datos obtenidos
          login(tokenFromUrl, parsedUser);
        } catch (error) {
          console.error(
            "Error al procesar datos del usuario desde la URL:",
            error,
          );
        }
      } else {
        console.error(
          "No se encontraron datos en la URL. Verificando localStorage...",
        );

        const token = localStorage.getItem("accessToken");
        const userData = localStorage.getItem("user");

        //console.log("Token desde localStorage:", token);
        //console.log("Datos del usuario desde localStorage:", userData);

        if (token && userData) {
          const parsedUser = JSON.parse(userData);
          login(token, parsedUser);
        }
      }
    }
  }, [location, login]);

  const loadLayout = (Layout, Page) => {
    return (
      <Layout>
        <Page />
      </Layout>
    );
  };

  // Defensa en profundidad: el panel admin solo lo renderizan usuarios con
  // role "admin". Aunque el LoginForm ya rechaza a no-admins, un token viejo de
  // no-admin en localStorage de una sesion previa NO debe dar acceso. getMe
  // devuelve { user: {...} }, asi que el rol vive en user.user.role.
  const isAdmin = String(user?.user?.role || "").toLowerCase() === "admin";

  return (
    <Routes>
      {!user || !isAdmin ? (
        <Route path="*" element={<Auth />} />
      ) : (
        <>
          <Route
            path="tiptap-test" // <-- 2. AJUSTA EL PATH PARA QUE SEA RELATIVO
            element={loadLayout(AdminLayout, TiptapTestPage)}
          />
          {/* Todas las rutas de administración */}
          <Route
            path="planes"
            element={loadLayout(AdminLayout, Page_PlanesEduca)}
          />
          <Route
            path="billings"
            element={loadLayout(AdminLayout, Page_Billings)}
          />
          {/*
            Switchover Etapa 7 (M05): la ruta legacy `/admin/users` ahora
            renderiza el panel v2 directamente. URLs antiguas no se rompen.
            El componente `Users` legacy sigue existiendo en el codigo pero
            no esta enrutado — pendiente de limpiar en Etapa 8.
          */}
          <Route path="users" element={loadLayout(AdminLayout, UsersV2)} />
          <Route path="users-v2" element={loadLayout(AdminLayout, UsersV2)} />
          {/* F6 M2 - Panel comercial de leads (lista + filtros) */}
          <Route path="leads" element={loadLayout(AdminLayout, Page_Leads)} />
          {/* F6 M3 - Detalle de un lead. /:id captura ObjectId Mongo. */}
          <Route
            path="leads/:id"
            element={loadLayout(AdminLayout, Page_LeadDetail)}
          />
          {/*
            Drip M3.3 - El editor del drip vive ahora como pestana "Correos
            funnel" dentro del panel comercial. La URL legacy redirige para
            no romper bookmarks (patron M08/M11).
          */}
          <Route
            path="drip"
            element={<Navigate to="/admin/leads?tab=drip" replace />}
          />
          {/*
            M08 - Agrupacion Educajob: una sola pagina con 5 tabs
            (blog / cursos / menu front / newsletter / usuarios cursos).
            Las URLs legacy redirigen al tab correspondiente para no
            romper bookmarks.
          */}
          <Route
            path="educajob"
            element={loadLayout(AdminLayout, Page_Educajob)}
          />
          <Route path="blog" element={<RedirectEducajobTab tab="blog" />} />
          <Route
            path="courses"
            element={<RedirectEducajobTab tab="courses" />}
          />
          <Route path="menu" element={<RedirectEducajobTab tab="menu" />} />
          <Route
            path="newsletter"
            element={<RedirectEducajobTab tab="newsletter" />}
          />
          <Route path="usersdb" element={<RedirectEducajobTab tab="users" />} />
          <Route
            path="salas"
            element={loadLayout(AdminLayout, MozillaHubRooms)}
          />
          <Route
            path="virtualscenes"
            element={loadLayout(AdminLayout, VirtualScenes)}
          />
          {/*
            M11 - Pagina contenedora REVI con tabs (Scenes + Formación).
            La URL legacy /admin/reviscene redirige al tab Scenes para
            no romper bookmarks.
          */}
          <Route path="revi" element={loadLayout(AdminLayout, Page_REVI)} />
          <Route path="reviscene" element={<RedirectReviSceneToTab />} />
          {/* Formacion K12: videos, guias PDF e info de uso de la plataforma
              para profesores e instituciones de Educa360. Coleccion propia,
              separada de la Formacion de REVI. */}
          <Route
            path="formacion"
            element={loadLayout(AdminLayout, Page_Formacion)}
          />
          <Route
            path="video360"
            element={loadLayout(AdminLayout, Page_Video360)}
          />
          {/* M09 - EducaFiles: mini-Drive admin sobre bucket
              gs://educa-files-edu360 con subida via signed URL. */}
          <Route
            path="educa-files"
            element={loadLayout(AdminLayout, Page_EducaFiles)}
          />
          {/* M13 F2.7 - Live Games: catalogo gameKeys autorizados SDK Unity */}
          <Route
            path="livegames"
            element={loadLayout(AdminLayout, Page_LiveGames)}
          />
          <Route
            path="assistance"
            element={loadLayout(AdminLayout, Assistance)}
          />
          <Route
            path="clientdbcreator"
            element={loadLayout(AdminLayout, ClientDBcreator)}
          />
          <Route path="clientdb" element={loadLayout(AdminLayout, ClientDB)} />
          <Route
            path="register"
            element={loadLayout(AdminLayout, Page_Partner_Profile)}
          />
          <Route
            path="simulators"
            element={loadLayout(AdminLayout, Simulators)}
          />
          <Route path="servers" element={loadLayout(AdminLayout, Servers)} />
          <Route
            path="dashboard"
            element={loadLayout(AdminLayout, Page_Dashboard)}
          />
          <Route
            path="dashboard/partners"
            element={loadLayout(AdminLayout, Page_DashboardPartners)}
          />
          <Route
            path="dashboard/institutions"
            element={loadLayout(AdminLayout, Page_DashboardInstitutions)}
          />
          <Route
            path="dashboard/teachers"
            element={loadLayout(AdminLayout, Page_DashboardTeachers)}
          />
          <Route
            path="dashboard/students"
            element={loadLayout(AdminLayout, Page_DashboardStudents)}
          />
          <Route
            path="institution"
            element={loadLayout(AdminLayout, Page_Partner_Institution)}
          />
          {/*
            M06 Fase 4: la ruta canonica del detalle de institucion es ahora
            /admin/dashboard/institutions/:id (coherente con las listas).
            La ruta legacy /admin/institutions/:id se mantiene con redirect
            durante M06+M07 para no romper bookmarks.
          */}
          <Route
            path="dashboard/institutions/:id"
            element={loadLayout(AdminLayout, Page_InstitutionDetail)}
          />
          <Route
            path="institutions/:id"
            element={<RedirectInstitutionDetail />}
          />
          <Route path="*" element={loadLayout(AdminLayout, Page_Dashboard)} />{" "}
          {/* Ruta por defecto para /admin (M08: era Blog, ahora dashboard) */}
        </>
      )}
    </Routes>
  );
}
